import { useLayoutEffect, useState } from 'react';
import { Document, Page } from 'react-pdf';

import type { PDFDoc } from '../types';

interface ReportsPdfPreviewProps {
  file: { data: Uint8Array } | null;
  numPages: null | number;
  onLoadError: (error: Error) => void;
  onLoadSuccess: (pdf: PDFDoc) => void;
  onSourceError: (error: Error) => void;
  opportunityId?: string;
  page: number;
  pdfError: null | string;
  pdfRenderKey: number;
}

const ReportsPdfPreview = ({
  file,
  numPages,
  onLoadError,
  onLoadSuccess,
  onSourceError,
  opportunityId,
  page,
  pdfError,
  pdfRenderKey
}: ReportsPdfPreviewProps) => {
  const [rightPanelWidth, setRightPanelWidth] = useState<number>(0);
  const [rightPanelEl, setRightPanelEl] = useState<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!rightPanelEl) return;

    const update = () => {
      setTimeout(() => {
        setRightPanelWidth(rightPanelEl.clientWidth);
      }, 500);
    };

    update();
    const raf = requestAnimationFrame(update);

    const ro = new ResizeObserver(() => {
      update();
    });
    ro.observe(rightPanelEl);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [rightPanelEl]);

  return (
    <div
      className="min-w-0 flex-1 overflow-auto bg-white p-4"
      ref={setRightPanelEl}
    >
      {pdfError ? (
        <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
          {pdfError}
        </div>
      ) : file ? (
        <div className="flex min-h-full w-full items-center justify-center">
          <Document
            error={
              <div className="text-muted-foreground text-sm">
                Failed to load report preview
              </div>
            }
            file={file}
            key={String(pdfRenderKey)}
            loading={
              <div className="text-muted-foreground text-sm">
                Loading report...
              </div>
            }
            noData={
              <div className="text-muted-foreground text-sm">No report loaded</div>
            }
            onLoadError={onLoadError}
            onLoadSuccess={onLoadSuccess}
            onSourceError={onSourceError}
          >
            <Page
              pageNumber={Math.min(page, numPages ?? page)}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              width={rightPanelWidth}
            />
          </Document>
        </div>
      ) : (
        <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
          {opportunityId
            ? 'No report loaded'
            : 'Missing deal context. Open reports from an investment deal.'}
        </div>
      )}
    </div>
  );
};

export default ReportsPdfPreview;
